import { MainTemplate } from "@/components/templates";
import {
  AuthContext,
  AuthStatus,
  SessionInfo,
  UserInfo,
} from "@/contexts/AuthContext";
import { SelectServiceButtons } from "@/components/organisms";
import Router from "next/router";
import { serviceLogin } from "@/libs/serviceLogin";
import { useContext, useEffect, useState } from "react";
import {
  Company,
  useServicesLazyQuery,
  useGetCompanyInfoLazyQuery,
} from "@/generated/graphql";
import { Services } from "@/models";
import { useCompanyLazyQuery } from "@/generated/graphql_billing";
import { KarteInfo } from "@/components/atoms/KarteInfo";
import { MigratePromptModal } from "@/components/atoms/MigratePromptModal";
import { MigrationBanner } from "@/components/atoms/MigrationBanner";
import useRollbarUtil from "../hooks/useRollbarUtil";
import config from "@/libs/config";
import { BannerVariant } from "@/libs/hooks/useMigratePrompt";

type Props = {
  sessionInfo: SessionInfo;
  userInfo: UserInfo;
  showMigrateModal?: boolean;
  bannerVariant?: BannerVariant;
  onDismissMigrateModal?: () => void;
  onDismissMigrationBanner?: () => void;
};

export const SelectServicePage = (props: Props) => {
  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const { authStatus } = useContext(AuthContext);
  const stage = config.STAGE;
  const isAdmin = props.userInfo.authority === "admin";
  const userSub = props.userInfo.sub;
  const companyId = props.userInfo.xCompanyID;
  const {
    showMigrateModal = false,
    bannerVariant = null,
    onDismissMigrateModal = () => {},
    onDismissMigrationBanner = () => {},
  } = props;
  const [companyInfo, setCompanyInfo] = useState<Company>(); // Company Info from ID Server
  const [isContractAdmin, setIsContractAdmin] = useState<boolean>(false);
  const [services, setServices] = useState(new Services());
  const [selectServiceButton, setSelectServiceButton] = useState(<></>);

  const [servicesQuery, { loading: getLoading }] = useServicesLazyQuery({
    onCompleted: (data) => {
      if (data.services) {
        setServices(new Services(data.services));
      }
    },
    onError: getNotifyErrorHandlerForGraphQL("Failed to get services"),
  });

  const [getCompanyInfoQuery, { loading: getCompanyInfoLoading }] =
    useGetCompanyInfoLazyQuery({
      onCompleted: (data) => {
        if (data?.company) {
          setCompanyInfo(data?.company as Company);
        }
      },
      onError: getNotifyErrorHandlerForGraphQL("Failed to get company info"),
    });

  const [companyQuery, { loading: getCompanyLoading }] = useCompanyLazyQuery({
    onCompleted: (data) => {
      if (data) {
        if (data.company.admins.find((admin) => admin.userSub === userSub)) {
          setIsContractAdmin(true);
        }
      }
    },
    onError: getNotifyErrorHandlerForGraphQL("Failed to get company"),
    context: {
      api: "billing",
    },
    variables: {
      uuid: companyId as never,
    },
  });

  useEffect(() => {
    if (authStatus === AuthStatus.SignedIn) {
      (() => {
        servicesQuery();
        companyQuery();
        getCompanyInfoQuery();
      })();
    }
  }, [authStatus]);

  const clickDXFButton = () => {
    serviceLogin(services.dxf.redirectURL, props.sessionInfo);
  };

  const clickInvoiceButton = () => {
    serviceLogin(services.invoice.redirectURL, props.sessionInfo);
  };

  const clickBFMButton = () => {
    serviceLogin(services.bfm.redirectURL, props.sessionInfo);
  };

  const clickLoanButton = () => {
    serviceLogin(services.loan.redirectURL, props.sessionInfo);
  };

  // FIXME 不要になったら削除する
  const clickInvoiceLocalhostButton = () => {
    serviceLogin("http://localhost:18081/xid/callback", props.sessionInfo);
  };

  // FIXME 不要になったら削除する
  const clickBFMLocalhostButton = () => {
    serviceLogin(
      "http://localhost:3000/mfbank/auth/xid/callback",
      props.sessionInfo
    );
  };

  const clickBFMV2LocalhostButton = () => {
    serviceLogin(
      `http://localhost:3000/${config.SUBDOMAIN}`,
      props.sessionInfo
    );
  };

  const clickBFMV2StgButton = () => {
    serviceLogin(
      `https://bfm.x.test.mfw.work/${config.SUBDOMAIN}`,
      props.sessionInfo
    );
  };

  const clickCFMStagingButton = () => {
    serviceLogin(
      "https://mfbank.cfm.x.test.mfw.work/xid/callback",
      props.sessionInfo
    );
  };

  const clickCFMLocalhostButton = () => {
    serviceLogin("http://localhost:18082/xid/callback", props.sessionInfo);
  };

  const clickManageUsersButton = () => {
    Router.push("/manage-users");
  };

  useEffect(() => {
    setSelectServiceButton(
      <SelectServiceButtons
        isAdminUsersAtMikatanoID={isAdmin && isContractAdmin}
        companyId={companyId}
        displayCompanyId={companyInfo?.displayCompanyId}
        stage={stage}
        clickInvoiceButton={clickInvoiceButton}
        clickDXFButton={clickDXFButton}
        clickBFMButton={clickBFMButton}
        clickLoanButton={clickLoanButton}
        clickManageUsersButton={clickManageUsersButton}
        clickInvoiceLocalhostButton={clickInvoiceLocalhostButton}
        clickBFMLocalhostButton={clickBFMLocalhostButton}
        clickBFMV2StagingButton={clickBFMV2StgButton}
        clickBFMV2LocalhostButton={clickBFMV2LocalhostButton}
        clickCFMLocalhostButton={clickCFMLocalhostButton}
        clickCFMStagingButton={clickCFMStagingButton}
      />
    );
    return;
  }, [services, isContractAdmin, isAdmin, companyInfo]);

  return (
    <MainTemplate
      title={`${config.BANK_NICKNAME} Mikatano シリーズ サービス一覧`}
    >
      <KarteInfo />
      {bannerVariant && (
        <MigrationBanner
          variant={bannerVariant}
          onClose={onDismissMigrationBanner}
        />
      )}
      {!getLoading &&
        !getCompanyLoading &&
        !getCompanyInfoLoading &&
        selectServiceButton}
      <MigratePromptModal
        active={showMigrateModal}
        onClickMigrate={() => Router.push("/migrate-users")}
        onClickLater={onDismissMigrateModal}
      />
    </MainTemplate>
  );
};
